import { ReactElement, ReactNode } from 'react';
import {
  Dialog,
  DialogProps,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogContentText,
  styled,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { CommonDialogProps } from '../types';
import { ContentModalActions } from '../ContentModalActions';

const StyledDialog = styled(Dialog)<{ backdropBlurIntensity?: number }>(({ backdropBlurIntensity = 0 }) => ({
  backdropFilter: `blur(${backdropBlurIntensity}px)`,
  '& .MuiDialog-paper': {
    padding: 0,
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(3),
  paddingBottom: theme.spacing(1),
  fontSize: 16,
}));

const StyledDialogContent = styled(DialogContent)(() => ({
  fontSize: 15,
  lineHeight: 1.3,
  '& .MuiDialogContentText-root': {
    fontSize: 'inherit',
  },
}));

const DialogActionsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  paddingTop: 0,
}));

/**
 * Props for the ContentModal component.
 * Supports button overrides, and flexible content types.
 */
export type ContentModalProps = CommonDialogProps &
  Omit<DialogProps, 'open' | 'onClose' | 'maxWidth' | 'fullWidth'> & {
    /**
     * Additional actions in the title.
     */
    titleActions?: ReactNode;

    /**
     * Whether the modal is currently open.
     */
    open: boolean;

    /**
     * Function to close the modal (used for both cancel and close button).
     */
    onClose?: () => void;

    /**
     * Function to call on confirm action
     * Ignored if not provided.
     */
    onConfirm?: (...args: unknown[]) => unknown;
  };

export const ContentModal = ({
  dialogTitle,
  titleActions,
  dialogContent,
  confirmButtonText,
  confirmButtonProps,
  cancelButtonText = 'Close',
  disableConfirmButton,
  hideDialogActions = false,
  showCloseButton,
  onClose,
  onConfirm,
  open,
  modalMaxWidth = 'sm',
  fullWidth = true,
  backdropBlurIntensity,
  ...rest
}: ContentModalProps): ReactElement => (
  <StyledDialog
    open={open}
    onClose={onClose}
    maxWidth={modalMaxWidth}
    fullWidth={fullWidth}
    backdropBlurIntensity={backdropBlurIntensity}
    {...rest}
  >
    {dialogTitle && (
      <StyledDialogTitle variant="h4" display="flex" alignItems="center" gap={1}>
        {dialogTitle}
        {titleActions}
        {showCloseButton && (
          <Box ml="auto">
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        )}
      </StyledDialogTitle>
    )}

    <StyledDialogContent>
      {typeof dialogContent === 'string' ? (
        <DialogContentText fontSize={15}>{dialogContent}</DialogContentText>
      ) : (
        dialogContent
      )}
    </StyledDialogContent>

    {!hideDialogActions && (
      <DialogActionsContainer>
        <ContentModalActions
          onConfirm={onConfirm}
          onClose={onClose}
          confirmButtonText={confirmButtonText}
          confirmButtonProps={confirmButtonProps}
          cancelButtonText={cancelButtonText}
          disableConfirmButton={disableConfirmButton}
        />
      </DialogActionsContainer>
    )}
  </StyledDialog>
);
